import { useState, useEffect } from 'react';
import { TopBar } from '../../components/feature/TopBar';
import { Sidebar } from '../../components/feature/Sidebar';

export default function PowerFlowPage() {
  const [powerFlowData, setPowerFlowData] = useState<any>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('realtime');
  const [animationEnabled, setAnimationEnabled] = useState(true);

  // Simular datos de la API Fronius PowerFlow
  useEffect(() => {
    const fetchData = () => {
      setIsLoading(true);
      setTimeout(() => {
        const mockData = {
          P_PV: Math.floor(Math.random() * 3000) + 1000,
          P_Load: Math.floor(Math.random() * 2500) + 800,
          P_Grid: Math.floor(Math.random() * 1000) - 500,
          rel_Autonomy: Math.floor(Math.random() * 40) + 60,
          rel_SelfConsumption: Math.floor(Math.random() * 30) + 70,
          Battery: {
            SOC: Math.floor(Math.random() * 40) + 60,
            P_Battery: Math.floor(Math.random() * 600) - 300
          }
        };
        setPowerFlowData(mockData);
        setLastUpdate(new Date());
        setIsLoading(false);
      }, 1000);
    };

    fetchData();
  }, []);



  const FlowArrow = ({ direction, animated = true }: { direction: 'up' | 'down' | 'left' | 'right', animated?: boolean }) => {
    const arrowClass = {
      up: 'ri-arrow-up-line',
      down: 'ri-arrow-down-line',
      left: 'ri-arrow-left-line',
      right: 'ri-arrow-right-line'
    };

    return (
      <div className={`${animated && animationEnabled ? 'animate-pulse' : ''} transition-all duration-300`}>
        <i className={`${arrowClass[direction]} text-2xl text-blue-500`}></i>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 ml-64">
          <TopBar title="Flujo de Energía" lastUpdate={lastUpdate} />
          <div className="p-6">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Cargando datos de flujo de energía...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Flujo de Energía" lastUpdate={lastUpdate} />

        <div className="p-6 space-y-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl p-8">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Flujo de Energía en Tiempo Real</h1>
                <p className="text-blue-100 max-w-xl">
                  Visualiza cómo se distribuye la energía entre tus paneles, hogar, batería y la red eléctrica.
                </p>
              </div>
              <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-md p-2 rounded-xl border border-white/20">
                <label className="flex items-center space-x-2 px-3 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={animationEnabled}
                    onChange={(e) => setAnimationEnabled(e.target.checked)}
                    className="rounded border-blue-300 text-blue-500 focus:ring-blue-500 bg-transparent"
                  />
                  <span className="text-sm text-white font-medium">Animaciones</span>
                </label>
                <div className="h-6 w-px bg-white/20"></div>
                <select
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  className="bg-transparent border-none text-white text-sm font-medium focus:ring-0 cursor-pointer"
                >
                  <option value="realtime" className="text-gray-900">Tiempo Real</option>
                  <option value="5min" className="text-gray-900">Últimos 5 min</option>
                  <option value="1hour" className="text-gray-900">Última hora</option>
                  <option value="today" className="text-gray-900">Hoy</option>
                </select>
              </div>
            </div>
          </div>

          {/* Diagrama de Flujo Principal */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>

            <div className="relative py-12">
              {/* Grid de posicionamiento */}
              <div className="grid grid-cols-3 gap-8 items-center min-h-[400px]">

                {/* Paneles Solares */}
                <div className="flex flex-col items-center space-y-6 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-yellow-400 blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                    <div className="relative bg-gradient-to-br from-yellow-400 to-orange-500 p-8 rounded-3xl shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 transition-all duration-300 transform hover:-translate-y-1">
                      <i className="ri-sun-line text-white text-5xl mb-3 block text-center"></i>
                      <div className="text-center text-white">
                        <p className="text-sm font-medium opacity-90 mb-1">Producción</p>
                        <p className="text-3xl font-bold tracking-tight">{powerFlowData?.P_PV || 0}W</p>
                      </div>
                    </div>
                  </div>

                  {/* Flecha hacia casa */}
                  <div className="flex flex-col items-center space-y-2">
                    <FlowArrow direction="down" animated={powerFlowData?.P_PV > 0} />
                    <span className="text-sm font-medium text-gray-500 bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                      Generando
                    </span>
                  </div>
                </div>

                {/* Casa/Consumo */}
                <div className="flex flex-col items-center space-y-6 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-blue-500 blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                    <div className="relative bg-gradient-to-br from-blue-500 to-indigo-600 p-8 rounded-3xl shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transition-all duration-300 transform hover:-translate-y-1">
                      <i className="ri-home-line text-white text-5xl mb-3 block text-center"></i>
                      <div className="text-center text-white">
                        <p className="text-sm font-medium opacity-90 mb-1">Consumo</p>
                        <p className="text-3xl font-bold tracking-tight">{powerFlowData?.P_Load || 0}W</p>
                      </div>
                    </div>
                  </div>

                  {/* Indicadores de autonomía */}
                  <div className="flex gap-3 w-full max-w-[280px]">
                    <div className="flex-1 bg-purple-50 border border-purple-100 p-3 rounded-xl text-center transition-colors hover:bg-purple-100">
                      <p className="text-xs text-purple-600 font-semibold uppercase tracking-wider mb-1">Autonomía</p>
                      <p className="text-xl font-bold text-purple-700">{powerFlowData?.rel_Autonomy || 0}%</p>
                    </div>
                    <div className="flex-1 bg-teal-50 border border-teal-100 p-3 rounded-xl text-center transition-colors hover:bg-teal-100">
                      <p className="text-xs text-teal-600 font-semibold uppercase tracking-wider mb-1">Autoconsumo</p>
                      <p className="text-xl font-bold text-teal-700">{powerFlowData?.rel_SelfConsumption || 0}%</p>
                    </div>
                  </div>
                </div>

                {/* Red Eléctrica */}
                <div className="flex flex-col items-center space-y-6 group">
                  <div className="relative">
                    <div className={`absolute inset-0 blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 ${powerFlowData?.P_Grid < 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <div className={`relative bg-gradient-to-br ${powerFlowData?.P_Grid < 0 ? 'from-green-500 to-emerald-600 shadow-green-500/20 hover:shadow-green-500/40' : 'from-red-500 to-rose-600 shadow-red-500/20 hover:shadow-red-500/40'} p-8 rounded-3xl shadow-lg transition-all duration-300 transform hover:-translate-y-1`}>
                      <i className={`${powerFlowData?.P_Grid < 0 ? 'ri-arrow-up-circle-line' : 'ri-arrow-down-circle-line'} text-white text-5xl mb-3 block text-center`}></i>
                      <div className="text-center text-white">
                        <p className="text-sm font-medium opacity-90 mb-1">Red Eléctrica</p>
                        <p className="text-3xl font-bold tracking-tight">{Math.abs(powerFlowData?.P_Grid || 0)}W</p>
                        <p className="text-xs opacity-80 mt-1 font-medium bg-black/20 px-2 py-0.5 rounded-full inline-block">
                          {powerFlowData?.P_Grid < 0 ? 'Exportando' : 'Importando'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Flecha hacia/desde casa */}
                  <div className="flex flex-col items-center space-y-2">
                    <FlowArrow
                      direction={powerFlowData?.P_Grid < 0 ? "up" : "down"}
                      animated={Math.abs(powerFlowData?.P_Grid || 0) > 0}
                    />
                    <span className={`text-sm font-medium px-3 py-1 rounded-full border ${powerFlowData?.P_Grid < 0 ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
                      {Math.abs(powerFlowData?.P_Grid || 0)}W
                    </span>
                  </div>
                </div>
              </div>

              {/* Batería (si está disponible) */}
              {powerFlowData?.Battery && (
                <div className="mt-12 flex justify-center">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-indigo-500 blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                    <div className="relative bg-gradient-to-br from-indigo-500 to-violet-600 p-6 rounded-2xl shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 transition-all duration-300 transform hover:-translate-y-1 min-w-[300px]">
                      <div className="flex items-center justify-between space-x-6">
                        <div className="bg-white/20 p-3 rounded-xl">
                          <i className="ri-battery-charge-line text-white text-3xl"></i>
                        </div>
                        <div className="text-white flex-1">
                          <p className="text-sm font-medium opacity-90 mb-1">Almacenamiento</p>
                          <div className="flex items-baseline justify-between">
                            <span className="text-2xl font-bold">{powerFlowData.Battery.SOC}%</span>
                            <span className="text-lg opacity-90 font-medium">{powerFlowData.Battery.P_Battery}W</span>
                          </div>
                          <div className="w-full bg-black/20 rounded-full h-1.5 mt-2">
                            <div className="bg-white h-1.5 rounded-full transition-all duration-500" style={{ width: `${powerFlowData.Battery.SOC}%` }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Métricas Detalladas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <i className="ri-scales-3-line text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Balance Energético</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600 font-medium text-sm">Producción Solar</span>
                  <span className="font-bold text-yellow-600">+{powerFlowData?.P_PV || 0}W</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600 font-medium text-sm">Consumo Casa</span>
                  <span className="font-bold text-blue-600">-{powerFlowData?.P_Load || 0}W</span>
                </div>
                <div className="flex justify-between items-center pt-2 px-3">
                  <span className="text-gray-900 font-bold text-sm">Balance Neto</span>
                  <span className={`font-bold text-lg ${(powerFlowData?.P_PV || 0) - (powerFlowData?.P_Load || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(powerFlowData?.P_PV || 0) - (powerFlowData?.P_Load || 0)}W
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                  <i className="ri-pie-chart-2-line text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Eficiencia</h3>
              </div>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600 font-medium text-sm">Autonomía</span>
                    <span className="font-bold text-purple-600">{powerFlowData?.rel_Autonomy || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-pink-500 h-2.5 rounded-full transition-all duration-500 shadow-sm"
                      style={{ width: `${powerFlowData?.rel_Autonomy || 0}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600 font-medium text-sm">Autoconsumo</span>
                    <span className="font-bold text-teal-600">{powerFlowData?.rel_SelfConsumption || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-teal-400 to-emerald-500 h-2.5 rounded-full transition-all duration-500 shadow-sm"
                      style={{ width: `${powerFlowData?.rel_SelfConsumption || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600">
                  <i className="ri-pulse-line text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Estado del Sistema</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50/50 rounded-xl border border-green-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-500/50"></div>
                    <span className="text-gray-700 font-medium text-sm">Sistema Operativo</span>
                  </div>
                  <i className="ri-checkbox-circle-fill text-green-500"></i>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50/50 rounded-xl border border-yellow-100">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${(powerFlowData?.P_PV || 0) > 0 ? 'bg-yellow-500 animate-pulse' : 'bg-gray-300'}`}></div>
                    <span className="text-gray-700 font-medium text-sm">Generación Solar</span>
                  </div>
                  {(powerFlowData?.P_PV || 0) > 0 && <i className="ri-sun-fill text-yellow-500"></i>}
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${powerFlowData?.Battery ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                    <span className="text-gray-700 font-medium text-sm">Batería Conectada</span>
                  </div>
                  {powerFlowData?.Battery && <i className="ri-battery-fill text-blue-500"></i>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}